import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { AssetProperty } from '@api';
import { Decimal } from 'decimal.js';
import { ComponentBaseDirective } from '../component.base';
import { Field } from '../telemetry/models/capability-model-data';
import { ControlType, IExtendedControl } from './models/IExtendedControl';
import { PropertiesGeneratorService } from './services/properties-generator.service';
@Component({
    selector: 'app-properties',
    templateUrl: './properties.component.html',
    styleUrls: ['./properties.component.scss'],
    providers: [PropertiesGeneratorService],
    standalone: false,
})
export class PropertiesComponent extends ComponentBaseDirective implements OnChanges {
    private el = inject(ElementRef);
    private propertiesGeneratorService = inject(PropertiesGeneratorService);

    @Input() public properties: Field[];
    @Input() public propertyValues: AssetProperty[];
    @Input() public changeMarkerActive: boolean;
    @Input() public isIOLinkDevice: boolean;
    @Input() public isCommand: boolean;
    @Output() public propertyChanged = new EventEmitter<boolean>();

    public form: UntypedFormGroup = new UntypedFormGroup({});
    public controlTypes = ControlType;
    public dirtyControls: AbstractControl[] = [];
    // The original values from backend
    public originalValues = new Map<string, string>();

    constructor() {
        super();
        const propertiesGeneratorService = this.propertiesGeneratorService;

        propertiesGeneratorService.initialize(this);
    }

    public get controls() {
        return Object.values(this.form.controls as unknown as IExtendedControl[]);
    }

    public get values() {
        const complexPropertyValues = this.propertiesGeneratorService.getComplexPropertyValues();

        const standardPropertValues = Object.keys(this.form.controls)
            .filter((key) => !(this.form.controls[key] as IExtendedControl).nameOfParent?.length && this.form.controls[key].dirty)
            .reduce(
                (obj, key) => ({
                    ...obj,
                    [key]: (this.form.controls[key] as IExtendedControl).hasGradientOrOffset ? this.revertGradientAndOffset(this.form.controls[key] as IExtendedControl) : this.form.controls[key].value,
                }),
                {}
            );

        Object.assign(standardPropertValues, complexPropertyValues);
        return standardPropertValues;
    }

    private revertGradientAndOffset(ctrl: IExtendedControl): Number {
        // use decimal to avoid floating point errors
        return new Decimal(ctrl.value).dividedBy(ctrl.gradient).minus(ctrl.offset).toNumber();
    }

    public isValid(): boolean {
        const invalidControl = this.el.nativeElement.querySelector('.form-control.ng-invalid');
        invalidControl?.focus();

        return this.form.valid;
    }

    public ngOnChanges(changes: SimpleChanges) {
        const hasNewProperties = changes?.properties != null;
        this.checkDirty(hasNewProperties);
    }

    public checkDirty(hasNewProperties: boolean) {
        // Create a list which fields are edited to skip this when realtime update is arriving
        for (const name of Object.keys(this.form.controls)) {
            const currentControl = this.form.controls[name] as IExtendedControl;

            if (currentControl.dirty) {
                this.dirtyControls = this.dirtyControls.filter((entity) => (entity as IExtendedControl).name !== name);
                this.dirtyControls.push(currentControl);
            }
        }

        this.propertiesGeneratorService.generateFormControls(hasNewProperties);
    }

    public resetDirtyState() {
        this.originalValues.clear();
        this.form.reset();
        this.dirtyControls = [];

        this.checkDirty(true);
    }

    public async checkForChange(formCtrlName: string) {
        const currentControl = this.form.controls[formCtrlName] as IExtendedControl;

        const allowChangeProperty = await this.propertiesGeneratorService.allowChangeProperty(currentControl, this.isIOLinkDevice);
        const formCtrlValue = currentControl.value;
        const originalValue = this.originalValues.get(formCtrlName);
        if (formCtrlValue == originalValue) {
            currentControl.markAsPristine();
        } else {
            currentControl.markAsDirty();
        }

        if (!allowChangeProperty) {
            currentControl.reset(originalValue);
            return;
        }

        this.propertyChanged.emit(currentControl.dirty);
    }
}
